// external dependencies
import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useSession } from "@supabase/auth-helpers-react";

// internal data providers
import { useAssetLibraryProvider, RecordingWidgetState } from "@/state/providers/AssetLibraryProvider";
import { FileUploadingState } from "@/common/utils/types";

// internal components
import WaitingView from "./WaitingView";
import RecordingView from "./RecordingView";
import UploadView from "./UploadView";
import HDDialog from "@/common/components/HDDialog";
import { toast } from "react-toastify";
import { isRecordingError } from "@/common/hooks/useAudioRecorder";
import DownloadIcon from "@icons/download-01.svg?react";
import MicrophoneIcon from "@icons/microphone-01.svg?react";
import { useTour } from "@/state/providers/TourProvider";
import { useAccountContext } from "@/state/providers/AccountProvider";
import { readQuickStartMinimized, writeQuickStartMinimized } from "./quickStartMinimizedPersistence";
import { useIsNarrowViewport } from "@/common/hooks/useIsNarrowViewport";
import { useAppSelector } from "@/common/hooks/useRedux";
import { selectSelectedTableRecordIds } from "@/state/redux/patientTableRecordsSlice";

// Minimum dwell on UPLOADING so the new multi-line copy is reliably read on fast uploads.
// Without this, fast networks flip UPLOADING → SAVED in under a frame and the vet only ever
// sees the brief SAVED message, which is the duplicate-record trigger EN-542 is fixing.
const MIN_UPLOADING_DWELL_MS = 3000;

const RecordingWidget = () => {
    // app state
    const location = useLocation();
    const { activeTour } = useTour();
    const isTourActiveRef = useRef(!!activeTour);
    isTourActiveRef.current = !!activeTour;

    // internal component state
    const [uploadProgress, setUploadProgress] = useState(0);
    const [audioTime, setAudioTime] = useState("0:00");
    const [newPatientRecordId, setNewPatientRecordId] = useState<string | null>(null);
    const previousLocation = useRef(location.pathname);

    // make size of recording stop button pulse when recording
    const [pulseScale, setPulseScale] = useState(1); // Default scale for stop recording button

    // state for local storage error modal
    const [showDownloadModal, setShowDownloadModal] = useState(false);

    // a utility function for converting seconds to a string
    // returns seconds in 0:00 format
    const secondsToString = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
    };

    // ------------------------------
    // PROVIDERS, DATA, AND STATE

    /**
     * Audio Recording Provider
     * hook for managing widget state, audio recording, file upload, document select, patient data
     **/
    const {
        // audio recording controls
        recorderControls: { audioStream, recordingTime, recordingState },
        // recording widget controls
        recordingWidgetControls: {
            recordingWidgetState,
            setRecordingWidgetState,
            setRenderRecordingWidget,
            renderRecordingWidget,
            handleFinishRecording,
            cancelInFlightStartRecording,
        },
        // patient data controls
        recordingPatientControls: { patientRecordId },
        // file upload controls
        recordingFileUploadControls: {
            uploadProgress: uploadProviderProgress,
            uploadingState,
            failedToUploadOrStoreRecording,
        },
        phoneCallWidgetControls: { renderPhoneCallWidget },
    } = useAssetLibraryProvider();

    const isRecordingStateError = isRecordingError(recordingState);

    // EN-1414: the clinic can turn the floating buttons off. Only the idle Quick Start goes away;
    // a recording or upload in progress (any state but WAITING) always shows, as does a tour.
    // EN-1494: it also steps aside while visits are selected in the list, where on a short screen
    // (a phone in landscape) it covered the bulk bar's buttons. Clearing the selection brings it back.
    const { accountData } = useAccountContext();
    const visitsSelected = useAppSelector(selectSelectedTableRecordIds).length > 0;
    const hideIdleQuickStart =
        (accountData?.meta_data?.floating_buttons_hidden === true || visitsSelected) &&
        recordingWidgetState === RecordingWidgetState.WAITING &&
        !activeTour;

    const session = useSession();
    const userId = session?.user?.id;
    // Below EN-1147's narrow-viewport breakpoint, a first-time (no stored preference) load
    // defaults to minimized so the panel doesn't cover the calendar grid on a phone. Once a
    // user has explicitly expanded or minimized it, that stored choice always wins.
    const isNarrowViewport = useIsNarrowViewport();
    const [isMinimized, setIsMinimized] = useState<boolean>(() =>
        userId ? readQuickStartMinimized(userId, isNarrowViewport) : isNarrowViewport,
    );
    // The lazy initializer above only runs once, at mount. AccountProvider.tsx documents a
    // real race where session pops in with no user right after a magic-link login — if that
    // race means userId was still undefined at mount, the stored preference above is never
    // read. Re-read it as soon as a real userId shows up, so a late-resolving session doesn't
    // permanently strand the widget on the viewport-only fallback.
    const syncedUserIdRef = useRef<string | undefined>(userId);
    // Tracks whether the user has expanded/minimized locally since mount, and what they chose.
    // A tap during the race above can't be persisted yet (setMinimized below has no userId to
    // write under), so without this the re-sync effect would overwrite that just-chosen state
    // with a stale (or default) storage read the moment userId resolves. isMinimizedRef mirrors
    // isMinimized itself, kept only by setMinimized (its sole setter besides this effect), so the
    // effect can read the latest value without depending on it and re-firing on every toggle.
    const hasLocalOverrideRef = useRef(false);
    const isMinimizedRef = useRef(isMinimized);
    useEffect(() => {
        if (!userId || syncedUserIdRef.current === userId) return;
        syncedUserIdRef.current = userId;
        if (hasLocalOverrideRef.current) {
            // Persist the pre-userId toggle now instead of discarding it via a storage read.
            writeQuickStartMinimized(userId, isMinimizedRef.current);
            return;
        }
        setIsMinimized(readQuickStartMinimized(userId, isNarrowViewport));
    }, [userId, isNarrowViewport]);

    const setMinimized = (minimized: boolean) => {
        hasLocalOverrideRef.current = true;
        isMinimizedRef.current = minimized;
        setIsMinimized(minimized);
        if (userId) writeQuickStartMinimized(userId, minimized);
    };

    // ------------------------------
    // WIDGET STATE HANDLERS

    // useEffect to determine which pages the widget should be rendered on
    useEffect(() => {
        if (location.pathname !== previousLocation.current) {
            // Closes the EN-1383 warning of a start parked on it (including an error-state retry that
            // never entered LOADING), so confirming it cannot record into the previous page's visit.
            // Also flags the start as cancelled, so a start still connecting (LOADING) aborts its
            // transition to RECORDING and tears down partial state after `await startRecording`
            // resolves. Calling handleFinishRecording() here would race with that start and leave a
            // phantom timer / armed watchdog.
            const abortedWarnedStart = cancelInFlightStartRecording();
            if (recordingWidgetState === RecordingWidgetState.LOADING) {
                setRecordingWidgetState(RecordingWidgetState.WAITING);
                // Nothing was recording if the start was only waiting on the warning; the closing modal is the feedback.
                if (!abortedWarnedStart) {
                    toast.warning("Recording stopped because you navigated away from the page");
                }
            } else if (
                recordingWidgetState === RecordingWidgetState.RECORDING ||
                recordingWidgetState === RecordingWidgetState.PAUSED
            ) {
                handleFinishRecording();
                setRecordingWidgetState(RecordingWidgetState.WAITING);
                toast.warning("Recording stopped because you navigated away from the page");
            }
        }

        // reset the widget state when navigating
        if (location.pathname === "/") {
            setRecordingWidgetState(RecordingWidgetState.WAITING);
        }

        // only default render the widget on the home page
        if (location.pathname === "/" && !renderRecordingWidget && !renderPhoneCallWidget) {
            setRenderRecordingWidget(true);
        } else if (location.pathname !== "/" && renderRecordingWidget) {
            // Don't hide the recording widget if there's an active recording
            const isRecordingActive =
                recordingWidgetState === RecordingWidgetState.RECORDING ||
                recordingWidgetState === RecordingWidgetState.PAUSED;
            if (!isRecordingActive) {
                setRenderRecordingWidget(false);
            }
        }

        // store the previous location
        previousLocation.current = location.pathname;

        // Only run on location or phone call widget render changes
    }, [location.pathname, renderPhoneCallWidget]);

    // sets the audio time string when recording time changes
    useEffect(() => {
        if (recordingTime >= 0) {
            setAudioTime(secondsToString(recordingTime));
        }
    }, [recordingTime]);

    const uploadingEnteredAtRef = useRef<number | null>(null);

    // transition widget state to SAVED after file is uploaded
    useEffect(() => {
        // Track when UPLOADING was entered (for the dwell timer below). Also reset displayed
        // progress to 0 on entry — the SAVED cleanup normally zeroes it, but a direct
        // UPLOADING → WAITING transition (e.g. navigating to "/") skips that cleanup, leaving
        // stale progress that the no-regress clamp below would then pin high on the next run.
        if (recordingWidgetState === RecordingWidgetState.UPLOADING && uploadingEnteredAtRef.current === null) {
            uploadingEnteredAtRef.current = Date.now();
            setUploadProgress(0);
        } else if (recordingWidgetState !== RecordingWidgetState.UPLOADING) {
            uploadingEnteredAtRef.current = null;
        }

        // While uploading, clamp displayed progress to 80% so the bar stays visibly in motion
        // through the minimum-dwell window. Never let the bar regress (provider can reset to 0
        // on retry — the user would see an empty bar under the "Saving your clip…" copy).
        if (
            recordingWidgetState === RecordingWidgetState.UPLOADING &&
            uploadingState === FileUploadingState.uploading
        ) {
            setUploadProgress((prev) => Math.max(prev, Math.min(uploadProviderProgress, 80)));
        }

        // if widget is uploading and upload is complete, transition to SAVED after the dwell
        if (recordingWidgetState === RecordingWidgetState.UPLOADING && uploadingState === FileUploadingState.inactive) {
            const enteredAt = uploadingEnteredAtRef.current ?? Date.now();
            const remaining = Math.max(0, MIN_UPLOADING_DWELL_MS - (Date.now() - enteredAt));

            if (remaining === 0) {
                setUploadProgress(100);
                setRecordingWidgetState(RecordingWidgetState.SAVED);
                return;
            }

            // hold the bar at 80% during the dwell, then jump to 100% on transition
            setUploadProgress(80);
            const timeout = setTimeout(() => {
                setUploadProgress(100);
                setRecordingWidgetState(RecordingWidgetState.SAVED);
            }, remaining);
            return () => clearTimeout(timeout);
        }
    }, [recordingWidgetState, uploadingState, uploadProviderProgress]);

    useEffect(() => {
        if (uploadingState === FileUploadingState.local_storage_error && failedToUploadOrStoreRecording) {
            setShowDownloadModal(true);
        }
    }, [uploadingState, failedToUploadOrStoreRecording]);

    // transition widget state to WAITING and clear progress bar after SAVED dwell
    // (delay auto-dismiss while a tour is active so the Visit button stays visible, but cap at 60s)
    // SAVED auto-dismiss is 9s (EN-542) so the confirmation message has time to be read.
    // isTourActive is read from a ref so tour start/end doesn't re-run the main effect and trigger
    // cleanup (which clears newPatientRecordId, breaking the Visit button).
    // A separate effect below handles re-evaluating the timer when the tour ends.
    useEffect(() => {
        if (recordingWidgetState !== RecordingWidgetState.SAVED) return;

        const delay = isTourActiveRef.current ? 60000 : 9000;
        const timeout = setTimeout(() => {
            setRecordingWidgetState(RecordingWidgetState.WAITING);

            // close the widget if not on the home page
            if (location.pathname !== "/" && renderRecordingWidget) {
                setRenderRecordingWidget(false);
            }
        }, delay);

        // cleanup by clearing timeout and progress bar
        return () => {
            clearTimeout(timeout);
            setUploadProgress(0);
            setNewPatientRecordId(null);
        };
    }, [recordingWidgetState, location.pathname, renderRecordingWidget]);

    // When a tour ends while in SAVED state, dismiss the widget after 3s instead of waiting
    // the full 60s. This is a separate effect to avoid clearing newPatientRecordId on tour
    // start/end (which would break the Visit button during the tour).
    // Only fires on the transition from tour-active → tour-ended (not on normal non-tour saves).
    const wasTourActiveRef = useRef(false);
    useEffect(() => {
        if (activeTour) {
            wasTourActiveRef.current = true;
            return;
        }
        // Only run the dismiss timer if a tour was previously active (just ended)
        if (!wasTourActiveRef.current) return;
        // Tour ended but widget isn't in SAVED state — reset ref without starting a timer
        if (recordingWidgetState !== RecordingWidgetState.SAVED) {
            wasTourActiveRef.current = false;
            return;
        }
        wasTourActiveRef.current = false;
        const timeout = setTimeout(() => {
            setRecordingWidgetState(RecordingWidgetState.WAITING);
            if (location.pathname !== "/" && renderRecordingWidget) {
                setRenderRecordingWidget(false);
            }
        }, 3000);
        return () => clearTimeout(timeout);
    }, [activeTour, recordingWidgetState]);

    useEffect(() => {
        if (!audioStream || recordingWidgetState === RecordingWidgetState.PAUSED || isRecordingStateError) return;

        // create audio context and analyser
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;

        // connect audio stream to analyser
        const source = audioContext.createMediaStreamSource(audioStream);
        source.connect(analyser);

        // store frequency data in an array
        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const updateScale = () => {
            analyser.getByteFrequencyData(dataArray);

            // calculate average volume
            const avgVolume = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;

            // map volume to scale factor (adjust min/max as needed)
            const scale = (avgVolume / 255) * 1; // normalize between 0.0 and 1.0
            setPulseScale(scale);

            requestAnimationFrame(updateScale);
        };

        updateScale();

        return () => {
            source.disconnect();
            analyser.disconnect();
            audioContext.close();
        };
    }, [audioStream, recordingWidgetState, isRecordingStateError]);

    // store new patient id when recording is saved
    useEffect(() => {
        if (patientRecordId) {
            setNewPatientRecordId(patientRecordId);
        }
    }, [patientRecordId]);

    /**
     * Download the recording file
     * handler for the download button in the confirm modal
     */
    const handleDownload = (audioFile: File) => {
        try {
            const url = URL.createObjectURL(audioFile);
            const link = document.createElement("a");
            link.href = url;
            link.download = audioFile.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setShowDownloadModal(false);
        } catch (error) {
            toast.error("Failed to download audio file");
            console.error("Download error:", error);
        }
    };

    // ------------------------------
    // VIEWS AND RENDERING

    // render component state based on widget state
    const recordingWidget = () => {
        switch (recordingWidgetState) {
            case RecordingWidgetState.WAITING:
                return <WaitingView onMinimize={() => setMinimized(true)} isNarrowViewport={isNarrowViewport} />;
            case RecordingWidgetState.RECORDING:
                return <RecordingView pulseScale={pulseScale} audioTime={audioTime} />;
            case RecordingWidgetState.PAUSED:
                return <RecordingView pulseScale={pulseScale} audioTime={audioTime} />;
            case RecordingWidgetState.LOADING:
                return <RecordingView pulseScale={pulseScale} audioTime={audioTime} />;
            case RecordingWidgetState.UPLOADING:
                return (
                    <UploadView
                        uploadProgress={uploadProgress}
                        newPatientRecordId={newPatientRecordId}
                        setNewPatientRecordId={setNewPatientRecordId}
                    />
                );
            case RecordingWidgetState.SAVED:
                return (
                    <UploadView
                        uploadProgress={uploadProgress}
                        newPatientRecordId={newPatientRecordId}
                        setNewPatientRecordId={setNewPatientRecordId}
                    />
                );
            case RecordingWidgetState.UPLOADING_ERROR:
                return (
                    <UploadView
                        uploadProgress={uploadProgress}
                        newPatientRecordId={newPatientRecordId}
                        setNewPatientRecordId={setNewPatientRecordId}
                    />
                );
        }
    };

    // render the recording widget
    // the panel sizes to its content (EN-1499), so the mic bar and any banner never clip
    return (
        <>
            {renderRecordingWidget &&
                !hideIdleQuickStart &&
                (recordingWidgetState === RecordingWidgetState.WAITING && isMinimized && !activeTour ? (
                    <button
                        type="button"
                        aria-label="Expand Quick Start"
                        onClick={() => setMinimized(false)}
                        className="fixed bottom-[156px] right-[22px] z-40 h-12 w-12 rounded-full bg-shadow-900 shadow flex items-center justify-center hover:scale-110 transition-transform duration-200"
                    >
                        <MicrophoneIcon className="w-5 h-5 text-white" />
                    </button>
                ) : (
                    <div
                        data-tour="start-recording"
                        className={`left-0 right-24 sm:inset-x-auto sm:w-[360px] sm:right-24 fixed bottom-0 sm:bottom-5 bg-shadow-900 rounded-t-2xl sm:rounded-lg shadow flex flex-col items-center gap-4 z-50
                        h-auto`}
                    >
                        {recordingWidget()}
                    </div>
                ))}
            {failedToUploadOrStoreRecording && (
                <HDDialog
                    id="download-recording-modal"
                    isOpen={showDownloadModal}
                    handleClose={() => setShowDownloadModal(false)}
                    closeOnClickOutside={false}
                    header="Save Your Recording"
                    footer={
                        <div>
                            <button
                                type="button"
                                className="primary-button"
                                onClick={() => handleDownload(failedToUploadOrStoreRecording)}
                            >
                                <DownloadIcon className="w-4 h-4" />
                                Save Recording
                            </button>
                        </div>
                    }
                >
                    <>
                        <p className="text-md text-gray-700 font-semibold">
                            Your recording was unable to be uploaded or saved locally.
                        </p>
                        <p className="text-md text-gray-700">
                            Please save it to your device so you can upload it when you are back online.
                            <span className="font-semibold"> If you do not save it, it will be lost.</span>
                        </p>
                    </>
                </HDDialog>
            )}
        </>
    );
};

export default RecordingWidget;
